import { create } from 'zustand';
import { useAccount } from '../account/store.ts';
import { api } from '../api/instance.ts';
import { flagsFor, has } from '../plans/features.ts';
import type { SyncService } from '../services/sync.ts';
import { hasPendingSave } from '../state/autosave.ts';
import { useDocument } from '../state/documentStore.ts';
import { toast } from '../state/feedback.ts';
import { db } from '../storage/db.ts';
import { storage } from '../storage/index.ts';
import { type ProjectFile, referencedBlobs } from '../storage/record.ts';
import { breadcrumb } from '../telemetry/sentry.ts';
import { SyncEngine } from './engine.ts';
import { syncHooks } from './hooks.ts';
import { DexieSyncStore } from './store.ts';
import type { ProjectSync, SyncBadge } from './types.ts';

export interface SyncUiState {
  status: 'off' | 'idle' | 'syncing' | 'offline' | 'error';
  lastSyncAt: number | null;
  lastError: string | null;
  updateRequired: boolean;
  rows: Record<string, ProjectSync>;
  pendingShas: Set<string>;
}

export const useSync = create<SyncUiState>(() => ({
  status: 'off',
  lastSyncAt: null,
  lastError: null,
  updateRequired: false,
  rows: {},
  pendingShas: new Set(),
}));

const EDIT_DEBOUNCE = 2_000;
const INTERVAL = 60_000;

/** Plan check for background sync (docs/spec/08: Free has no `sync`). */
export function canSyncScope(scope: string | null): boolean {
  const { me } = useAccount.getState();
  return !!scope && has(flagsFor(me, scope), 'sync');
}

export function badgeOf(row: ProjectSync | undefined, file: ProjectFile | null, pending: Set<string>): SyncBadge {
  if (!row || (row.baseRev === 0 && !canSyncScope(row.scope))) return 'local';
  if (row.requiresUpdate) return 'requiresUpdate';
  if (row.remoteDeleted) return 'deletedRemotely';
  if (row.quotaBlocked) return 'notBackedUp';
  if (file && referencedBlobs(file).some((s) => pending.has(s))) return 'uploading';
  if (row.dirty || row.pendingDelete || row.baseRev === 0) return 'pending';
  return 'synced';
}

/** Re-opens the project in the editor with the server version, keeping the current tool. */
function reloadOpen(file: ProjectFile): void {
  const doc = useDocument.getState();
  if (doc.projectId !== file.id || hasPendingSave()) return;
  const { tool, view } = doc;
  doc.open(file);
  doc.dispatch({ type: 'setTool', tool });
  doc.dispatch({ type: 'setView', view });
}

export class SyncServiceImpl implements SyncService {
  readonly engine: SyncEngine;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private interval: ReturnType<typeof setInterval> | undefined;
  private started = false;
  private readonly off: (() => void)[] = [];

  constructor() {
    this.engine = new SyncEngine({
      api,
      store: new DexieSyncStore(),
      blobs: {
        get: (sha) => storage().blobs.getLocal(sha),
        put: (b) => storage().blobs.put(b),
        has: (sha) => storage().blobs.has(sha),
      },
      userId: () => useAccount.getState().session?.userId ?? null,
      upload: async (url, headers, body) => {
        const res = await fetch(url, { method: 'PUT', headers, body });
        if (!res.ok) throw new Error(`Upload failed (HTTP ${res.status}).`);
      },
      download: async (url) => {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Download failed (HTTP ${res.status}).`);
        return res.blob();
      },
      onRemoteProject: (file) => reloadOpen(file),
      onRemoteDelete: (id) => {
        if (useDocument.getState().projectId === id) toast('This project was deleted on another device.');
      },
      onStateChange: () => void this.refreshUi(),
      canSync: (scope) => canSyncScope(scope),
    });
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    storage().blobs.setRemoteFallback((sha) => this.engine.ensureBlob(sha));
    const userId = useAccount.getState().session?.userId;
    if (userId) void this.engine.indexBlobs(userId);
    this.off.push(
      syncHooks.on(
        'saved',
        (id) => void this.engine.localSaved(id).then((t) => t && this.requestSync('edit', EDIT_DEBOUNCE)),
      ),
      syncHooks.on('created', (id) => {
        const scope = useAccount.getState().activeScope;
        if (scope && canSyncScope(scope))
          void this.engine.localSaved(id, scope).then(() => this.requestSync('edit', EDIT_DEBOUNCE));
      }),
      syncHooks.on('deleted', (id) => void this.engine.localDeleted(id).then(() => this.requestSync('edit', 500))),
    );
    const onVisible = () => document.visibilityState === 'visible' && this.requestSync('foreground');
    const onOnline = () => this.requestSync('online');
    document.addEventListener('visibilitychange', onVisible);
    addEventListener('focus', onVisible);
    addEventListener('online', onOnline);
    this.off.push(() => {
      document.removeEventListener('visibilitychange', onVisible);
      removeEventListener('focus', onVisible);
      removeEventListener('online', onOnline);
    });
    this.interval = setInterval(() => document.visibilityState === 'visible' && this.requestSync('timer'), INTERVAL);
    useSync.setState({ status: 'idle' });
    this.requestSync('signin');
  }

  stop(): void {
    this.started = false;
    clearTimeout(this.timer);
    clearInterval(this.interval);
    for (const f of this.off.splice(0)) f();
    storage().blobs.setRemoteFallback(null);
    useSync.setState({ status: 'off', rows: {}, pendingShas: new Set(), lastError: null });
  }

  requestSync(_reason: string, delayMs = 0): void {
    if (!this.started) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.syncNow(), delayMs);
  }

  async syncNow(): Promise<void> {
    if (!this.started || !useAccount.getState().session) return;
    const run = async () => {
      useSync.setState({ status: 'syncing' });
      const report = await this.engine.syncNow();
      const offline = report.errors.some((e) => /offline|Network/i.test(e));
      if (report.errors.length && !offline)
        breadcrumb(
          'sync',
          'Sync finished with errors',
          { count: report.errors.length, first: report.errors[0] },
          'warning',
        );
      useSync.setState({
        status: report.errors.length ? (offline ? 'offline' : 'error') : 'idle',
        lastError: report.errors[0] ?? null,
        lastSyncAt: report.errors.length ? useSync.getState().lastSyncAt : Date.now(),
        updateRequired: this.engine.updateRequired,
      });
      await this.refreshUi();
    };
    // One tab syncs at a time (Web Locks); other tabs see results through Dexie liveQuery.
    const locks = (navigator as Navigator & { locks?: LockManager }).locks;
    if (locks) await locks.request('better-lights-sync', run);
    else await run();
  }

  async backup(ids: string[], scope: string): Promise<void> {
    for (const id of ids) await this.engine.localSaved(id, scope);
    await this.refreshUi();
    this.requestSync('manual');
  }

  async restore(id: string): Promise<ProjectFile | null> {
    const f = await this.engine.restore(id);
    await this.refreshUi();
    this.requestSync('manual', 200);
    return f;
  }

  async moveProject(id: string, toScope: string): Promise<void> {
    await this.syncNow();
    await this.engine.move(id, toScope, (pid, scope) => api.moveProject(pid, scope));
    await this.refreshUi();
    this.requestSync('manual', 300);
  }

  badgeFor(file: ProjectFile): SyncBadge {
    const s = useSync.getState();
    return badgeOf(s.rows[file.id], file, s.pendingShas);
  }

  private async refreshUi(): Promise<void> {
    const userId = useAccount.getState().session?.userId;
    if (!userId) return;
    const rows = await db().projectSync.where('userId').equals(userId).toArray();
    const jobs = await db().blobJobs.where('userId').equals(userId).toArray();
    useSync.setState({
      rows: Object.fromEntries(rows.map((r) => [r.id, r])),
      pendingShas: new Set(jobs.map((j) => j.sha)),
      updateRequired: this.engine.updateRequired,
    });
  }
}
